/*Procuret JS Library 1.0.1 compiled 2026-03-04 00:16:25.904384*/

/* Procuret JS - Entity Headline Type */


class PR_EntityHeadline {

    constructor(
        entityId,         // String encoded integer
        legalEntityName,  // String
    ) {

        this._entityId = entityId;
        this._legalEntityName = legalEntityName;

        return;

    }

    get entityId() { return this._entityId; }
    get legalEntityName() { return this._legalEntityName; }

    static decode(data) {
        return new PR_EntityHeadline(
            data['entity_id'],
            data['legal_entity_name']
        );
    }

    static optionallyDecode(data) {
        if (!data) { return null; }
        return PR_EntityHeadline.decode(data);
    }

}

/* A theoretical payment amount, and the number of months over which that
payment would be made, if a customer successfully applied for a Procuret
Instalment Plan */
class PR_ProspectivePayment {

    static get path() { return '/credit/prospective-payment'; }
    static get listPath() { return PR_ProspectivePayment.path + '/list'; }

    constructor(
        payment,     // String (String encoded decimal number)
        rawCycle,    // Number (Integer)
        supplierId,  // String
        periods,     // Number (Integer)
        currency     // PR_Currency
    ) {

        this._payment = payment;
        this._rawCycle = rawCycle;
        this._supplierId = supplierId;
        this._periods = periods;
        this._currency = currency;
    
        return;
    }

    // Number (The number of months over which payment would be made)
    get periods() { return this._periods; }

    // PR_Amount (The monthly payment amount)
    get amount() {
        return new PR_Amount(
            this._payment,
            this._currency
        );
    }

    // The supplier for which this PR_ProspectivePayment is valid
    get supplierId() { return this._supplierId; }

    static decode(data) {
        return new PR_ProspectivePayment(
            data['payment'],
            data['cycle'],
            data['supplier_id'],
            data['periods'],
            PR_Currency.decode(data['currency'])
        );
    }

    /*
    Retrieve a single PR_ProspectivePayment for given parameters. To use this
    method, you must know a valid months value in advance. If you don't know a
    valid months value in advance, prefer .retrieveAllAvailable.
    */
    static retrieve(
        callback,     // Function<Error?, PR_ProspectivePayment?>
        principal,    // String - A number, the prospective loan princpal
        supplierId,   // String - A string-encoded 64-bit integer
        denomination, // PR_Currency - The loan principal denomination
        months,       // Number - integer number of months over which to pay
        endpoint=null,// Optional<String> - override the Procuret API endpoint
        session=null  // Optional<Session> (not required in this context)
    ) {

        try {

            const targets = [
                new PR_QueryTerm('cycle', 1),  // in advance
                new PR_QueryTerm('supplier_id', supplierId),
                new PR_QueryTerm('principle', principal),
                new PR_QueryTerm('periods', months),
                new PR_QueryTerm('denomination', denomination.indexid)
            ];
    
            const parameters = new PR_QueryString(targets);
    
            PR_ApiRequest.make(
                PR_ProspectivePayment.path,
                'GET',
                parameters,
                null,
                (error, data) => {
                    if (error != null) { callback(error, null); return; }
                    callback(null, PR_ProspectivePayment.decode(data));
                    return;
                },
                session,
                endpoint,
                null,
                true   // optionalAuth
            );
        
        } catch(error) {

            callback(error, null);

        }

        return;

    }

    /*
    Retrieve all available PR_ProspectivePayment for given parameters. This is
    a convenient way to display all potential instalment plan payment amounts
    to a potential applicant.
    */
    static retrieveAllAvailable(
        callback,     // Function<Error?, Array<ProspectivePayment>?>
        principal,    // String - A number, the prospective loan principal
        denomination, // PR_Currency - The principal denomination
        supplierId,   // String - a String-encoded 64-bit integer
        endpoint=null,// Optional<String> - override the Procuret API endpoint
        session=null  // Optional<Session> (not required in this context)
    ) {

        try {

            const targets = [
                new PR_QueryTerm('cycle', 1),  // in advance
                new PR_QueryTerm('supplier_id', supplierId),
                new PR_QueryTerm('principal_magnitude', principal),
                new PR_QueryTerm('denomination', denomination.indexid)
            ]

            const parameters = new PR_QueryString(targets);

            PR_ApiRequest.make(
                PR_ProspectivePayment.listPath,
                'GET',
                parameters,
                null,
                (error, data) => {
                    if (error != null) { callback(error, null); return; }
                    callback(
                        null,
                        data.map((d) => {
                            return PR_ProspectivePayment.decode(d);
                        })
                    );
                    return;
                },
                session,
                endpoint,
                null,
                true     // optionalAuth
            );

        } catch(error) {

            callback(error, null);

        }

        return;

    }

}

/* Procuret JS - Instalment Link Open Type */


class PR_InstalmentLinkOpen {

    static get path() { return PR_InstalmentLink.path + '/open'; }

    constructor(
        sequence,    // Number (Integer)
        created      // String (encoded time)
    ) {

        this._sequence = sequence;
        this._created = created;

        return;

    }

    static optionallyDecode(data) {
        if (!data) { return null; }
        return new PR_InstalmentLinkOpen(
            data['sequence'],
            data['created']
        )
    }

    static decode(data) {
        const result = PR_InstalmentLinkOpen.optionallyDecode(data);
        if (!result) { throw Error('Unexpectedly null data'); }
        return result;
    }

    static decodeMany(data) {
        return data.map((d) => { return PR_InstalmentLinkOpen.decode(d); })
    }

}

/* Procuret JS - Order Type */

class PR_Order {

    static get ASCENDING_KEY() { return 'ascending'; }
    static get DESCENDING_KEY() { return 'descending'; }

    constructor(key) {
        this._key = key;
        return;
    }

    get key() { return this._key }

    static get ASCENDING() { return new PR_Order('ascending'); }
    static get DESCENDING() { return new PR_Order('descending'); }

    static get enumerations() { return [
        PR_Order.ASCENDING,
        PR_Order.DESCENDING
    ];}

    static withKey(
        key  // String
    ) {  // -> PR_Order
        const Self = PR_Order
        for (let i = 0; i < Self.enumerations.length; i++) {
            if (Self.enumerations[i].key == key) {
                return Self.enumerations[i]
            }
            continue
        }
        throw Error('Unknown key ' + key);
    }

}

/* Procuret JS - QueryString */

class PR_QueryString {

    constructor(
        url_parameters  // Array<PR_QueryTerm>
    ) {

        if (url_parameters.length === undefined) {
            throw Error('parameters do not appear to be an array')
        }
        this._parameters = url_parameters;
        return;

    }

    get query() { 
        let query = '?';
        for (let i = 0; i < this._parameters.length; i++) {
            if (i === 0) {
                query += this._parameters[i].string;
                continue;
            }
            query += '&' + this._parameters[i].string;
            continue;
        }
        return query;
    }

}

/* Procuret JS - Response Type */


class PR_Response {

    static decode(
        error,      // Error?
        data,       // Object?
        callback,   // Function(Error?, T?)
        outputType  // T<Having .decode(:Object<String: Any>) method>
    ) {
        let result = null;
        if (error != null) { callback(error, null); return; }
        if (!data) { callback(null, null); return; }
        try { result = outputType.decode(data); }
        catch (error) { callback(error, null); return; }
        callback(null, result);
        return;
    }

    static decodeMany(
        error,      // Error?
        data,       // Array<Object>?
        callback,   // Function(Error?, T?)
        outputType  // T<Having .decode(:Object) method> 
    ) {

        let result = null;
        if (error != null) { callback(error, null); return; }
        if (data == null) { callback(null, null); return; }
        try { result = data.map((d) => { return outputType.decode(d); }); }
        catch (error) { callback(error, null); return; }
        callback(null, result);
        return;

    }
 
}
/* Procuret JS - API request Type */


class PR_ApiRequest {

    static get _KEY_HEADER() { return 'x-procuret-api-key' }
    static get _SESSION_ID_HEADER() { return 'x-procuret-session-id' }
    static get _JSON_HEADER() { return 'application/json;charset=UTF-8'; }
    static get _LIVE_ENDPOINT() { return 'https://procuret.com/api' }
    static get _QUOTE_EXPRESSION() { new RegExp(
        /(\b\d{13,128}\b)(?!\.)(?!\/)(?!\%)(?!")(?!:)(?!-)/g
    ); }

    static make(
        path,                // String e.g. '/humans'
        method,              // String e.g. 'GET'
        parameters=null,     // Optional<PR_QueryString>
        data=null,           // Object e.g. {'hello': 'world' } (optional)
        callback,            // Function(PR_ApiError?, Data?),
        session=null,        // Optional Session
        apiEndpoint=null,    // Optional String
        withoutAuth=false,   // Boolean (send request with no authentication)
        optionalAuth=false,  // Boolean (send with or without auth)
        suppressError=false, // Do not send ErrorReport on failure
        doNotEscapeInt=false,// Do not attempt to escape 64 bit integers
        throwOnGet404=false  // Throw an error on 404 rather than return null
    ) {

        const Self = PR_ApiRequest;

        try {

            if (!path) { throw Error('Cannot make request to falsy path'); }
            if (['GET', 'UPDATE', 'DELETE', 'POST', 'PUT'].indexOf(
                method
            ) < 0) {
                throw Error('Method appears invalid: ' + method);
            }
    
            const throwOn404 = (() => {
                if (method != 'GET') { return true; }
                return throwOnGet404;
            })();
    
            const request = new XMLHttpRequest();
    
            const summary = {
                requestData: data ? JSON.stringify(data) : null,
                requestParameters: parameters,
                requestPath: path,
                requestMethod: method,
                suppressError: suppressError
            }
    
            request.onreadystatechange = () => {
                Self._parseResponse(
                    request,
                    callback,
                    summary,
                    doNotEscapeInt,
                    throwOn404
                );
                return;
            }
    
            const endpoint = Self._chooseApiEndpoint(apiEndpoint);
            const url = Self._buildUrl(
                path,
                parameters,
                endpoint
            );
    
            request.open(method, url, true);
    
            function applyAuth(
                session   // Optional<Session>
            ) {
                if (withoutAuth) { return; }
                const apiKey = Self._chooseApiKey(session, optionalAuth);
                const sessionId = Self._chooseSessionId(session, optionalAuth);
                if ((!apiKey || !sessionId) && optionalAuth) { return }
                request.setRequestHeader(Self._SESSION_ID_HEADER, sessionId);
                request.setRequestHeader(Self._KEY_HEADER, apiKey);
                return; 
            }
    
            applyAuth(session);
    
            if (data) {
                request.setRequestHeader('content-type', Self._JSON_HEADER);
                request.send(JSON.stringify(data));
            } else {
                request.send();
            }
    
            return;
    

        } catch(error) { callback(error, null); return; }

    }

    static integerSafeJSONParse(string) {
        const quotedBody = string.replace(
            PR_ApiRequest._QUOTE_EXPRESSION,
            '\"$&\"'
        );
        return JSON.parse(quotedBody);
    }

    static _buildUrl(
        path,          // String
        parameters,    // PR_QueryString
        apiEndpoint    // String
    ) {
        const base = apiEndpoint + path;
        if (parameters) { return base + parameters.query; }
        return base;
    }

    static _parseResponse(
        request,
        callback,
        summary,
        doNotEscapeInt,
        throwOn404
    ) {

        const state = request.readyState;
        const status = request.status;
        const Self = PR_ApiRequest;

        if (state === 4 && status === 200) {
            let result = null;
            try {
                const rawText = request.responseText;
                if (doNotEscapeInt) { result = JSON.parse(rawText); }
                else { result = Self.integerSafeJSONParse(rawText); }
            } catch(error) {
                callback(error, null);
                return;
            }

            callback(null, result);
            return;

        } else if (state === 4 && status === 404) {

            if (!throwOn404) { callback(null, null); return; }

            let errorContent = null;

            try {
                errorContent = Self.integerSafeJSONParse(request.responseText);
            } catch (error) {
                const e = new PR_ApiError(
                    status,
                    null,
                    summary
                );
                callback(e, null);
                return
            }

            const error = new PR_ApiError(status, errorContent, summary);
            error.dispatchEvent();
            callback(error, null);

            return;

        } else if (state === 4 && status !== 200 ) {

            let errorContent = null;

            try {
                errorContent = Self.integerSafeJSONParse(request.responseText);
            } catch (error) {
                const e = new PR_ApiError(
                    status,
                    null,
                    summary
                );
                callback(e, null);
                return
            }

            const error = new PR_ApiError(status, errorContent, summary);
            error.dispatchEvent();
            callback(error, null);
            return;
        }

        return;

    }

    static _chooseApiKey(override, optionalAuth=false) {
        if (override) { return override.apiKey; }
        if (optionalAuth) { return null; }
        throw Error('No API Key available. Supply Session instance to PR_ApiRe\
quest.make()');
    }

    static _chooseSessionId(override, optionalAuth=false) {
        if (override) { return override.sessionId; }
        if (optionalAuth) { return null; }
        throw Error('No Session ID available. Supply Session instance to PR_Ap\
iRequest.make()');
    }

    static _chooseApiEndpoint(override) {
        if (override) { return override; }
        return PR_ApiRequest._LIVE_ENDPOINT;
    }

}

/* Procuret JS - Url Parameter Type */


class PR_QueryTerm {

    constructor(key, value) {
        this._key = key;
        this._value = PR_QueryTerm._interpretValue(value);
        return;
    }

    get string() {
        return this._key + '=' + encodeURIComponent(this._value);
    }

    static _interpretValue(value) {
        if (value === true) { return 'true' }
        if (value === false) { return 'false' }
        return value;
    }

    static compactPush(
        key,      // String
        value,    // Optional<Any>
        array     // Array<Self>
    ) { // -> Array<Self>

        if (value == null) { return array; }

        array.push(new PR_QueryTerm(key, value));

        return array;

    }

    static cp(k, v, a) { return PR_QueryTerm.compactPush(k, v, a); }

}

/* Procuret JS - Enumeration Type */

class PR_Enumeration {

    static get enumerations() { throw Error('Not implemented'); }

    constructor(
        indexid,     // Integer
        name         // String
    ) {

        this._indexid = indexid;
        this._name = name;

        return;

    }

    get indexid() { return this._indexid; }
    get name() { return this._name; }

    equalTo(other) {
        if (other == null) { return false; }
        return other.indexid === this._indexid;
    }
    notEqualTo(other) { return !this.equalTo(other); }

    isIn(others) {
        if (!others) { return false; }
        for(let i = 0; i < others.length; i++) {
            if (this.equalTo(others[i])) { return true; }
            continue;
        }
        return false;
    }
    
    isNotIn(others) { return !(this.isIn(others)); }

    static withId(indexid, type) {
        for (let i = 0; i < type.enumerations.length; i++) {
            const candidate = type.enumerations[i];
            if (candidate.indexid == indexid) { return candidate; }
            continue;
        }
        throw Error('Unknown indexid ' + indexid + ' for type enum ' + type);
    }

    static decode(data, type) {
        return PR_Enumeration.withId(data, type);
    }

    static optionallyDecode(data, type) {
        if (!data) { return null; }
        if (data == 'null') { return null; }
        return PR_Enumeration.decode(data, type);
    }

}

/* Procuret JS - API error class */

const PR_ERROR_INFO_KEY = 'error-information';
const PR_ERROR_CUSTOMER_INFORMATION = {
    400: "Procuret's servers could not understand the information sent by \
    your device. This likely indicates a bug in our application. Our support \
    team has been notified automatically, but please feel free to contact \
    us at support@procuret.com",
    500: "Procuret's servers encountered an error when processing information \
    sent by your device. There may be a bug in our systems, or they may be \
    experiencing a temporary service disruption. Our support team has been \
    notified automatically.",
    503: "Procuret's servers encountered a service disruption while processing \
    your request. This error may disappear if you try again in a few moments. \
    Our support team has been notified automatically. If this error persists, \
    please write to us at support@procuret.com.",
    401: "Procuret's servers could not identify you when processing \
    data sent by your device. This likely indicates a bug has crept into \
    our application. Please contact us at support@procuret.com.",
    403: "You don't appear to be authorised to perform the action you \
    were attempting.  This likely indicates a bug has crept into \
    our application. Please contact us at support@procuret.com.",
    429: "Procuret's servers have noticed unusually high activity levels \
    from your device or the network it is connected to. Please try again in \
    few minutes. If you continue to see this message, please contact \
    support@procuret.com",
    404: "Procuret's servers were unable to find a resource needed to \
    serve your request. This likely indicates a bug in our application. Our \
    support team has been notified automatically, but please feel free to \
    contact us at support@procuret.com",
    422: "Procuret was unable to process your request, due to a problem with \
the information sent by your device."
}
const PR_ERROR_FALLBACK_INFORMATION = "Procuret has encountered an error. There \
may be a bug in our systems, or they may be experiencing a temporary \
disruption. We will \
attempt to resolve the problem as quickly as possible";

class PR_ApiError extends Error {

    static get genericDescription() { return PR_ERROR_FALLBACK_INFORMATION; }

    constructor(
        code,                     // Integer
        data=null,                // Optional<Object>
        requestSummary=null       // Optional<Object> (Untyped)
    ) {

        const description = PR_ERROR_CUSTOMER_INFORMATION[code];
        let techInfo = (() => { 
            if (code != 0) { return 'Generic ' + String(code); }
            return 'Client side error';
        })();
        if (
            typeof(data) != 'undefined'
            && data != null
            && data[PR_ERROR_INFO_KEY]
        ) {
            techInfo = data[PR_ERROR_INFO_KEY];
        }
        const logMessage = 'API error (' + code + '), ' + techInfo;
        super(logMessage);

        const self = this;

        self._code = code;
        self._customerDescription = description
            || PR_ERROR_FALLBACK_INFORMATION;
        self._technicalDescription = techInfo;
        self._requestSummary = requestSummary;

        return

    }

    get code() { return this._code }
    get customerDescription() { return this._customerDescription }
    get technicalDescription() { return this._technicalDescription }


}

/* Procuret API JS - Amount JS Class */


class PR_Amount {

    constructor(
        magnitude,  // String decimal number e.g. "4,000" or "4000"
        currency    // PR_Currency
    ) {

        if (!currency) { throw Error('Cannot init w/ falsey PR_Currency'); }

        this._magnitude = magnitude;
        this._currency = currency;

        return;
    }

    get asNumber() { return new Number(this._magnitude); }
    get asLocaleString() { 
        return this.asNumber.toLocaleString(undefined, {
            minimumFractionDigits: this._currency.exponent
        });
    }
    get asSymbolisedString() {
        return this._currency.symbol + this.asLocaleString;
    }
    get asDenominatedString() {
        return this._currency.iso_4217 + ' ' + this.asLocaleString;
    }

    get magnitude() { return this._magnitude; }
    get denomination() { return this._currency; }

    get isGreaterThanZero() { return Number(this._magnitude) > 0; }

    rounded(decimalPlaces) {
        return new PR_Amount(
            Number(this._magnitude).toFixed(decimalPlaces),
            this._currency
        );
    }

    magnitudeIsGreaterThan(value) {
        return Number(this._magnitude) > value;
    }

    encode() {
        return {
            'magnitude': this._magnitude,
            'denomination': this._currency.indexid
        }
    }

    static decode(data) {
        return new PR_Amount(
            data['magnitude'],
            PR_Currency.decode(data['denomination'])
        );
    }

    static optionallyDecode(data) {  // -> Optional<Self>
        if (!data) { return null; }
        return PR_Amount.decode(data);
    }

}

/* Procuret JS - OrderBy Type */

class PR_OrderBy {

    constructor(key) {
        this._key = key;
        return;
    }

    get key() { return this._key; }

    static get enumerations() { throw Error('Not implemented'); }
    static get default() { throw Error('Not implemented'); }
    static get defaultOrder() { return PR_Order.DESCENDING; }

    static withKey(
        key,
        enumerations
    ) {
        for (let i = 0; i < enumerations.length; i++) {
            if (enumerations[i].key == key) {
                return enumerations[i];
            }
            continue;
        }
        throw Error('Unknown key ' + key);
    }

}

/* Procuret JS - Disposition Type */


class PR_Disposition {

    constructor(
        count,  // Integer
        limit,  // Integer
        offset,  // Integer
        sequence  // Integer
    ) {

        this._count = count;
        this._limit = limit;
        this._offset = offset;
        this._sequence = sequence;

        return;

    }

    get count() { return this._count; }
    get limit() { return this._limit; }
    get offset() { return this._offset; }
    get sequence() { return this._sequence; }

    static decode(data) {
        return new PR_Disposition(
            data['count'],
            data['limit'],
            data['offset'],
            data['sequence']
        );
    }

    static optionallyDecode(data) {
        if (data == null) { return null; }
        return PR_Disposition.decode(data);
    }

}

/* Procuret JS - Instalment Link Type */


class PR_InstalmentLink {

    static get path() { return '/instalment-link'; }
    static get listPath() { return PR_InstalmentLink.path + '/list'; }

    constructor(
        publicId,          // String
        supplier,          // PR_EntityHeadline
        created,           // String encoded time
        inviteeEmail,      // Optional<String>
        invoiceAmount,     // String
        invoiceIdentifier, // String
        opens,             // Array<PR_InstalmentLinkOpen>
        disposition,       // Optional<PR_Disposition>
        saleName,          // Number (Integer)
        allowEdit,         // Boolean
        denominationId     // Number (Integer)
    ) {

        this._publicId = publicId;
        this._supplier = supplier;
        this._created = created;
        this._inviteeEmail = inviteeEmail;
        this._invoiceAmount = invoiceAmount;
        this._invoiceIdentifier = invoiceIdentifier;
        this._opens = opens;
        this._disposition = disposition;
        this._saleName = saleName;
        this._allowEdit = allowEdit;
        this._denominationId = denominationId;

        return;

    }

    get publicId() { return this._publicId; }
    get supplier() { return this._supplier; }
    get created() { return this._created; }
    get inviteeEmail() { return this._inviteeEmail; }
    get invoiceAmount() { return this._invoiceAmount; }
    get invoiceIdentifier() { return this._invoiceIdentifier; }
    get opens() { return this._opens; }
    get disposition() { return this._disposition; }
    get saleName() { return this._saleName; }
    get allowEdit() { return this._allowEdit; }
    get denominationId() { return this._denominationId; }

    get denomination() {
        return PR_Currency.withId(this._denominationId);
    }

    get hasBeenOpened() { return this._opens.length > 0; }
    get openCount() { return this._opens.length; }

    get amount() { return new Amount(
        this._invoiceAmount,
        this.denomination
    ); }

    static optionallyDecode(data) {
        if (!data) { return null; }
        return new PR_InstalmentLink(
            data['public_id'],
            PR_EntityHeadline.decode(data['supplier']),
            ProcuretTime.decode(data['created']),
            data['invitee_email'],
            data['invoice_amount'],
            data['invoice_identifier'],
            PR_InstalmentLinkOpen.decodeMany(data['opens']),
            PR_Disposition.optionallyDecode(data['disposition']),
            data['sale_name'],
            data['allow_edit'],
            data['denomination_id']
        );
    }

    static decode(data) {
        const result = PR_InstalmentLink.optionallyDecode(data);
        if (!result) { throw Error('Unexpededly nil PR_InstalmentLink'); }
        return result;
    }

    static retrieve(
        publicId,      // String
        callback,      // Function<Error?, PR_InstalmentLink?> -> Void
        session=null   // Optional<Session>
    ) {

        PR_InstalmentLink.retrieveMany(
            (e, links) => {
                if (e) { callback(e, null); return; }
                if (!links) { callback(null, null); return; }
                callback(null, links[0]);
                return;
            },
            1,
            0,
            PR_Order.DESCENDING,
            PR_InstalmentLinkOrderBy.CREATED,
            null,
            null,
            null,
            session,
            publicId
        );

        return;

    }

    static retrieveMany(
        callback,           // Function<Error?, Array<PR_InstalmentLink>> -> Void
        limit=20,           // Integer
        offset=0,           // Integer
        order=PR_Order.DESCENDING,
        orderBy=PR_InstalmentLinkOrderBy.CREATED,
        textFragment=null,  // Optional<String>
        opened=null,        // Optional<Bool>
        supplierId=null,    // Optional<String>
        session=null,       // Optional<Session>
        publicId=null,      // Optional<String>
        accessibleTo=null   // Optional<String>
    ) {

        try {

            if (textFragment) { throw Error('text search not implemented'); }

            const Self = PR_InstalmentLink; const UP = PR_QueryTerm;
    
            const parameters = [
                new UP('limit', limit),
                new UP('offset', offset),
                new UP('order', order.key),
                new UP('order_by', orderBy.key)
            ]
    
            UP.compactPush('opened', opened, parameters);
            UP.compactPush('supplier_id', supplierId, parameters);
            UP.compactPush('public_id', publicId, parameters);
            UP.compactPush('accessible_to', accessibleTo, parameters);
    
            PR_ApiRequest.make(
                PR_InstalmentLink.listPath,
                'GET',
                new PR_QueryString(parameters),
                null,
                (e, d) => { PR_Response.decodeMany(e, d, callback, Self); },
                session
            );
    
            return;
    

        } catch (error) { callback(error, null); return; }

        return;

    }

    static create(
        callback,            // Function<Error?, PR_InstalmentLink?>
        supplierId,          // Integer
        amount,              // Amount
        identifier,          // String
        inviteeEmail,        // String
        communicate,         // Bool
        inviteePhone=null,   // Optional<String>
        saleName=null,       // Optional<SaleNomenclature>
        session=null         // Optional<Session>
    ) {

        const Self = PR_InstalmentLink;
        
        const payload = {
            'supplier_id': supplierId,
            'invoice_amount': amount.magnitude,
            'denomination': amount.denomination.indexid,
            'invoice_identifier': identifier,
            'invitee_email': inviteeEmail,
            'communicate': communicate,
            'nomenclature': saleName ? saleName.indexid : null,
            'invitee_phone': inviteePhone
        }

        PR_ApiRequest.make(
            PR_InstalmentLink.path,
            'POST',
            null,
            payload,
            (e, d) => { PR_Response.decode(e, d, callback, Self); },
            session
        );

        return;
    }

}



class PR_InstalmentLinkOrderBy extends PR_OrderBy {

    static get CREATED() { return new PR_InstalmentLinkOrderBy('created'); }

    static get default() { return PR_InstalmentLinkOrderBy.CREATED; }
    static get defaultOrder() { return PR_Order.DESCENDING; }

    static get enumerations() { return [
        PR_InstalmentLinkOrderBy.CREATED,
    ]}

    static withKey(
        key  // String
    ) {  // -> PR_InstalmentLinkOrderBy
        return PR_OrderBy.withKey(key, PR_InstalmentLinkOrderBy.enumerations);
    }
}

/* Procuret JS - PR_Currency Type */


class PR_Currency extends PR_Enumeration {

    static get enumerations() { return [
        _CURRENCY_AUD,
        _CURRENCY_NZD
    ];}

    constructor(
        indexid,       // Number (Integer)
        iso_4217,      // String
        name,          // String
        exponent,      // Number (Integer)
        symbol         // String
    ) {

        super(indexid, name)

        this._iso_4217 = iso_4217.toUpperCase();
        this._exponent = exponent;
        this._symbol = symbol;

        return;
    }

    get iso_4217() { return this._iso_4217; }
    get symbol() { return this._symbol; }
    get exponent() { return this._exponent; }

    static get AUD() { return _CURRENCY_AUD; }
    static get NZD() { return _CURRENCY_NZD; }

    static get allAvailable() {  // -> Array<Self>
        return [
            _CURRENCY_AUD,
            _CURRENCY_NZD
        ];
    }

    static decode(
        data  // Object<String, Any>
    ) {  // -> Self
        return new PR_Currency(
            data['indexid'],
            data['iso_4217'],
            data['name'],
            data['exponent'],
            data['symbol']
        );
    }

    static withId(
        indexid  // Integer
    ) {  // -> Self
        const Self = PR_Currency;
        if (indexid == Self.AUD.indexid) { return Self.AUD; }
        if (indexid == Self.NZD.indexid) { return Self.NZD; }
        throw Error('Unknown currency ' + indexid);
    }

}

const _CURRENCY_AUD = new PR_Currency(1, 'aud', 'Australian Dollar', 2, '$');
const _CURRENCY_NZD = new PR_Currency(2, 'nzd', 'New Zealand Dollar', 2, '$');

document.addEventListener('DOMContentLoaded', function() {
    // 1. Configuration
    const supplierId = '44573842558678361'; 
    const amountToFinance = "2000"; // The $2000 principal
    const durationMonths = 12;      // 12 months

    // 2. Calculation Call
    function setProcuretPricing(amountToFinance, supplierId, durationMonths) {
        if (typeof PR_ProspectivePayment !== 'undefined') {
            PR_ProspectivePayment.retrieve(
            (error, payment) => {
                const displayDiv = document.getElementById('procuret-payment-display');
                
                if (error) {
                console.error('Procuret Error:', error);
                displayDiv.innerHTML = '<p style="color:red;">Could not calculate payment. Please check Supplier ID.</p>';
                return;
                }

                // 3. Print the Result
                // .asSymbolisedString will print something like "$185.00"
                // .asDenominatedString will print something like "AUD 185.00"
                const monthlyAmount = payment.amount.asSymbolisedString;
                
                displayDiv.innerHTML = `
                <div class="procuret-result">
                    <p>Pay in ${durationMonths} payments of <strong>${monthlyAmount}</strong> with Procuret</p
                </div>
                `;
            },
            amountToFinance,
            supplierId,
            PR_Currency.AUD, // Change to PR_Currency.NZD if in New Zealand
            durationMonths
            );
        } else {
            console.error('Procuret library (procuret.js) not found. Ensure it is added to Assets.');
        }
    }
    
    //product__actual-price

    /**
     * Detects price changes even if Shopify replaces the entire span
     */
    const priceObserver = () => {
    // 1. Find the PARENT of the price span (usually a div with class 'price' or 'product__price')
    // If you aren't sure of the parent, 'document.body' works but is less efficient.
    const container = document.querySelector('.price') || document.body; 
    const targetId = 'product__actual-price';

    const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
        // We look for the specific ID inside any changes happening in the container
        const priceSpan = document.getElementById(targetId);
        
        if (priceSpan) {
            const currentPrice = priceSpan.textContent.trim();
            console.log("Price detected:", currentPrice);

            const priceString = currentPrice;

            const cleanNumber = parseFloat(priceString.replace(/[^\d.]/g, ''));
            const formatted = cleanNumber.toFixed(2); 

            console.log(formatted);
            // setProcuretPricing(formatted, supplierId, durationMonths); disable for now
        }
        });
    });

    // 'subtree: true' allows us to see changes deep inside the container
    observer.observe(container, {
        childList: true,
        subtree: true,
        characterData: true
    });

    console.log("Price observer is active on container.");
    };

    // Run after page load
    window.addEventListener('load', priceObserver);

    // setProcuretPricing(amountToFinance, supplierId, durationMonths); disable for now
});